import axios from 'axios'
import { useAuthStore } from '@/store/auth'

export type IntelAction = {
  action_id: string
  action_type: string
  title: string
  description: string
  confirmation_required: boolean
  preview?: string | null
}

export type IntelPlan = {
  summary: string
  actions: IntelAction[]
}

export type IntelActionResult = {
  action_id: string
  action_type: string
  status: 'success' | 'failed' | 'skipped'
  detail?: string | null
  ui_directives?: AdminIntelUIDirective[]
}

export type EmployeeCandidateOption = {
  employee_id: number
  label: string
  line: string
  company?: string | null
  rank?: string | null
  department?: string | null
  office?: string | null
  title?: string | null
  city?: string | null
  state?: string | null
}

export type EmployeeChoicePrompt = {
  choice_id: string
  query: string
  label: string
  options: EmployeeCandidateOption[]
  allow_create?: boolean
  seed_first_name?: string | null
  seed_last_name?: string | null
}

export type EmployeeChoiceSelection = {
  choice_id: string
  employee_id?: number | null
  create_new?: boolean
}

export type IntelHistoryEntry = {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export type AdminIntelUIDirective = {
  type: 'navigate' | 'toast' | 'modal'
  data?: Record<string, any>
}

export type IntelResponse = {
  session_id: string
  reply: string
  requires_confirmation: boolean
  plan?: IntelPlan | null
  plan_token?: string | null
  executed_actions: IntelActionResult[]
  employee_choices?: EmployeeChoicePrompt[]
  history: IntelHistoryEntry[]
  ui_directives?: AdminIntelUIDirective[]
}

export type IntelHistoryResponse = {
  session_id: string
  history: IntelHistoryEntry[]
}

export type IntelAttachment = {
  filename?: string | null
  content_type?: string | null
  data: string
}

type IntelPayload = {
  message?: string
  session_id?: string | null
  plan_token?: string | null
  approved_action_ids?: string[] | null
  rejected_action_ids?: string[] | null
  employee_choices?: EmployeeChoiceSelection[] | null
  attachments?: IntelAttachment[] | null
}

function authHeaders() {
  const auth = useAuthStore()
  return auth?.token ? { Authorization: `Bearer ${auth.token}` } : {}
}

export function sendIntelMessage(payload: IntelPayload) {
  return axios
    .post<IntelResponse>('/api/admin-intel/chat', payload, { headers: authHeaders() })
    .then((res) => res.data)
}

export function fetchIntelHistory(sessionId: string) {
  return axios
    .get<IntelHistoryResponse>(`/api/admin-intel/sessions/${sessionId}`, { headers: authHeaders() })
    .then((res) => res.data)
}

export function resetIntelHistory(sessionId: string) {
  return axios
    .delete<IntelHistoryResponse>(`/api/admin-intel/sessions/${sessionId}`, { headers: authHeaders() })
    .then((res) => res.data)
}
